/**
 * OSS 资源与媒体授权续签 Store
 *
 * 【设计原则与续签方案】：
 * 1. 资源上下文授权原则：
 *    - 前端提交 `resource_type + resource_id + asset_ids` 进行批量续签。
 *    - 前端不得提交 OSS path、bucket、旧签名 URL 或场景作为授权依据。
 *    - 仅允许前台已注册授权器的合法 `resource_type`，禁止前台提交 `INTERNAL`。
 *    - 单次请求限制：最多 20 个资源上下文，单个资源最多 100 个去重后的 `asset_id`。
 * 2. 页面级资源缓存 (assetCache, videoAuthCache)：仅作为【当前页面】渲染期间的临时资源 URL 与视频授权缓存，
 *    在路由/页面切换或组件卸载时调用 `clearCache()` 进行重置清空，避免跨页面缓存泄漏。
 * 3. 用户头像缓存 (userAvatarCache)：使用 `ACCOUNT` 资源类型续签，单独处理与独立存储，全局持久保留，不受页面切换 `clearCache()` 清理影响。
 * 4. 学习资源约束：视频课时需通过 `/learning/lessons/{id}/play-auth` 播放授权；详情/文章富文本资产不通过统一续签接口解析。
 * 5. 自动后台巡检续期 (Auto Renew Ticker)：注册登记当前页面活跃的资源上下文，5 秒自动巡检预热到期资产并静默发包续签。
 */

import { create } from 'zustand';
import {
  fetchResourceAssetAccess,
  requestEmbeddedVideoPlayAuth,
} from '@/service/api/learning';
import {
  fetchRenewResourceAccessUrls,
} from '@/service/api/upload';

/**
 * 默认 OSS 资源预热/提前续期缓冲时间（生产默认 120 秒，提前预热）
 */
export const DEFAULT_ASSET_PREHEAT_SECONDS = 120;

/** 默认未下发过期时间的新资源初始保鲜时长（生产默认 1800 秒，即 30 分钟） */
export const DEFAULT_ASSET_TTL_SECONDS = 1800;

/** 单次批量续签去重后的资产最大限制数量 */
export const MAX_BATCH_ASSET_COUNT = 100;

/** 单次批量续签资源上下文的最大限制数量 */
export const MAX_BATCH_RESOURCE_COUNT = 20;

/**
 * 计算/标准化资产绝对过期时间戳（单位：秒）
 * 若下发了有效 expiresAt，转换为绝对时间戳，否则按当前时间 + DEFAULT_ASSET_TTL_SECONDS 计算。
 */
export const calculateAssetExpiresAt = (expiresAt?: number | string): number => {
  const now = Math.floor(Date.now() / 1000);

  // 【测试模式】：当显式将 DEFAULT_ASSET_TTL_SECONDS 设为小于 300 秒（如 30 秒）时，
  // 强制覆盖为测试 TTL，以便于在任意界面快速观察自动化发包与续签效果
  if (DEFAULT_ASSET_TTL_SECONDS < 300) {
    return now + DEFAULT_ASSET_TTL_SECONDS;
  }

  const numExpires = typeof expiresAt === 'string' ? Number(expiresAt) : expiresAt;

  if (typeof numExpires === 'number' && !isNaN(numExpires) && numExpires > 0) {
    let expSec = numExpires > 1e11 ? Math.floor(numExpires / 1000) : numExpires;
    // 若传进来的是相对 TTL 秒数 (例如小于 1e8，如 3600、7200)，自动转换为当前绝对时间点
    if (expSec < 1e8) {
      expSec = now + expSec;
    }
    // 若后端下发的时间戳小于等于当前时间（已经处于过期状态），给予默认保鲜期，防止前端频繁死循环发包
    if (expSec <= now) {
      return now + DEFAULT_ASSET_TTL_SECONDS;
    }
    return expSec;
  }
  return now + DEFAULT_ASSET_TTL_SECONDS;
};

/** 前台允许的合法业务资源类型 (resource_type) */
export const RENEWABLE_RESOURCE_TYPES = [
  'ACCOUNT',
  'PERSONAL_SUBJECT',
  'ENTERPRISE_SUBJECT',
  'PROVIDER_PROFILE',
  'RESUME_PORTFOLIO',
  'DEMAND',
  'SOLUTION',
  'LEAD',
  'APPLICATION',
  'CATALOG_WORK',
  'COMMUNITY_POST',
  'COMMENT',
  'PROJECT',
  'CONTRACT',
  'REVENUE_STATEMENT',
  'REVENUE_DEDUCTION',
  'SETTLEMENT_PROFILE',
  'LEARNING_RESOURCE',
  'LEARNING_LESSON',
  'BANNER',
  'CHANNEL',
  'SUPPORT_TICKET',
  'SUPPORT_TICKET_FEEDBACK',
] as const;

export type RenewableResourceType = (typeof RENEWABLE_RESOURCE_TYPES)[number];

export interface AssetAccessCacheItem {
  asset_id: string;
  url: string;
  expires_at?: number;
}

export interface EmbeddedVideoAuthCacheItem {
  video_id: string;
  manifest?: string;
  play_session_id?: string;
  expires_at?: number;
}

export interface UserAvatarCacheItem {
  asset_id?: string;
  url: string;
  expires_at?: number;
}

export interface ActiveResourceContextItem {
  resource_type: string;
  resource_id: string;
  asset_ids: string[];
}

export interface AssetRenewState {
  assetCache: Record<string, AssetAccessCacheItem>;
  videoAuthCache: Record<string, EmbeddedVideoAuthCacheItem>;
  userAvatarCache: UserAvatarCacheItem | null;
  activeResourceContexts: Record<string, ActiveResourceContextItem>;
  autoRenewTimerId: ReturnType<typeof setInterval> | null;
  renewing: boolean;

  renewResourceAssets: (
    resourceId: string | number,
    assetIds: string[],
    options?: { onlyExpired?: boolean }
  ) => Promise<Api.Learning.ResourceAssetAccessItem[]>;

  renewEmbeddedVideoAuth: (
    videoId: string | number
  ) => Promise<Api.Learning.PlayManifestResponse>;

  renewSingleAsset: (
    assetId: string | number,
    resourceType?: RenewableResourceType | string,
    resourceId?: string | number
  ) => Promise<Api.Upload.RenewAccessURLResponse>;

  renewBatchResources: (
    items: Api.Upload.RenewResourceAccessURLsItemRequest[]
  ) => Promise<Api.Upload.RenewResourceAccessURLsResponse>;

  setUserAvatar: (avatar: UserAvatarCacheItem | null) => void;

  renewUserAvatar: (
    avatarAssetId: string | number,
    userId?: string | number
  ) => Promise<Api.Upload.RenewAccessURLResponse>;

  setAssetCacheItem: (assetId: string, url: string, expiresAt?: number) => void;

  isAssetExpired: (assetId: string, preheatSeconds?: number) => boolean;

  startAutoRenewTicker: () => void;
  stopAutoRenewTicker: () => void;
  triggerAutoRenewCheck: () => Promise<void>;

  clearPageCache: () => void;
  clearAllCache: () => void;
  clearCache: () => void;
}

export const useAssetRenewStore = create<AssetRenewState>((set, get) => ({
  assetCache: {},
  videoAuthCache: {},
  userAvatarCache: null,
  activeResourceContexts: {},
  autoRenewTimerId: null,
  renewing: false,

  renewResourceAssets: async (resourceId, assetIds, options) => {
    const validAssetIds = Array.from(new Set(assetIds.filter(Boolean)));
    if (!validAssetIds.length || !resourceId) return [];

    // 登记活动学习资源上下文，供后台轮询预热
    const targetResourceId = String(resourceId);
    const ctxKey = `LEARNING_RESOURCE:${targetResourceId}`;
    set((state) => {
      const existing = state.activeResourceContexts[ctxKey];
      const nextAssetIds = new Set(existing ? existing.asset_ids : []);
      validAssetIds.forEach((id) => nextAssetIds.add(id));
      return {
        activeResourceContexts: {
          ...state.activeResourceContexts,
          [ctxKey]: {
            resource_type: 'LEARNING_RESOURCE',
            resource_id: targetResourceId,
            asset_ids: Array.from(nextAssetIds),
          },
        },
      };
    });
    get().startAutoRenewTicker();

    // 如果设置了 onlyExpired，只处理即将过期的资产
    const pendingAssetIds = options?.onlyExpired
      ? validAssetIds.filter((id) => get().isAssetExpired(id))
      : validAssetIds;

    if (!pendingAssetIds.length) {
      return validAssetIds
        .map((id) => get().assetCache[id])
        .filter(Boolean)
        .map((item) => ({
          asset_id: item.asset_id,
          url: item.url,
          expires_at: item.expires_at,
        }));
    }

    set({ renewing: true });
    try {
      const res = await fetchResourceAssetAccess(resourceId, pendingAssetIds);
      const items = res.assets || [];

      set((state) => {
        const nextAssetCache = { ...state.assetCache };
        items.forEach((item) => {
          if (item.asset_id && item.url) {
            nextAssetCache[item.asset_id] = {
              asset_id: item.asset_id,
              url: item.url,
              expires_at: calculateAssetExpiresAt(item.expires_at),
            };
          }
        });
        return { assetCache: nextAssetCache, renewing: false };
      });

      return items;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewEmbeddedVideoAuth: async (videoId) => {
    if (!videoId) throw new Error('缺失视频标识');
    set({ renewing: true });
    try {
      const res = await requestEmbeddedVideoPlayAuth(videoId);
      const videoKey = String(videoId);
      set((state) => ({
        videoAuthCache: {
          ...state.videoAuthCache,
          [videoKey]: {
            video_id: videoKey,
            manifest: res.manifest,
            play_session_id: res.play_session_id,
            expires_at: calculateAssetExpiresAt(res.expires_at),
          },
        },
        renewing: false,
      }));
      return res;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewSingleAsset: async (assetId, resourceType, resourceId) => {
    const key = String(assetId);
    if (!key) return {};

    const targetResourceType = resourceType || 'ACCOUNT';
    const targetResourceId = String(resourceId || key);

    // 登记活动单资产上下文，保障巡检能够定期续发
    const ctxKey = `${targetResourceType}:${targetResourceId}`;
    set((state) => {
      const existing = state.activeResourceContexts[ctxKey];
      const nextAssetIds = new Set(existing ? existing.asset_ids : []);
      nextAssetIds.add(key);
      return {
        activeResourceContexts: {
          ...state.activeResourceContexts,
          [ctxKey]: {
            resource_type: targetResourceType,
            resource_id: targetResourceId,
            asset_ids: Array.from(nextAssetIds),
          },
        },
      };
    });
    get().startAutoRenewTicker();

    if (!get().isAssetExpired(key)) {
      const existing = get().assetCache[key];
      return { asset_id: key, access_url: existing?.url, expires_at: existing?.expires_at };
    }

    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({
        resources: [{ resource_type: targetResourceType, resource_id: targetResourceId, asset_ids: [key] }],
      });
      const successItem = res.items?.[0];
      if (successItem && successItem.access_url) {
        const expiresAt = calculateAssetExpiresAt(successItem.expires_at);
        set((state) => ({
          assetCache: { ...state.assetCache, [key]: { asset_id: key, url: successItem.access_url!, expires_at: expiresAt } },
          renewing: false,
        }));
        return { asset_id: key, access_url: successItem.access_url, expires_at: expiresAt };
      }
      set({ renewing: false });
      return { asset_id: key };
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewBatchResources: async (items) => {
    if (!items || !items.length) return { items: [], failed: [] };

    // 全量登记活动资源上下文，保障后续后台自动巡检能够捕获并预热刷新
    set((state) => {
      const nextContexts = { ...state.activeResourceContexts };
      items.forEach((item) => {
        if (!item.resource_type || !item.resource_id || !item.asset_ids?.length) return;
        const ctxKey = `${item.resource_type}:${item.resource_id}`;
        const existing = nextContexts[ctxKey];
        const mergedAssetIds = new Set(existing ? existing.asset_ids : []);
        item.asset_ids.forEach((id) => mergedAssetIds.add(String(id)));
        nextContexts[ctxKey] = {
          resource_type: item.resource_type,
          resource_id: String(item.resource_id),
          asset_ids: Array.from(mergedAssetIds),
        };
      });
      return { activeResourceContexts: nextContexts };
    });
    get().startAutoRenewTicker();

    // 1. 筛选出处于到期/预热状态的资产项
    const sanitizedItems: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    items.forEach((item) => {
      if (!item.resource_type || !item.resource_id || !item.asset_ids?.length || item.resource_type === 'INTERNAL') return;
      const uniqueAssetIds = Array.from(new Set(item.asset_ids.map(String)));
      const expiredAssetIds = uniqueAssetIds.filter((id) => get().isAssetExpired(id)).slice(0, MAX_BATCH_ASSET_COUNT);
      if (expiredAssetIds.length) {
        sanitizedItems.push({ resource_type: item.resource_type, resource_id: String(item.resource_id), asset_ids: expiredAssetIds });
      }
    });

    // 2. 按 resource_type:resource_id 进行深度归并，将同资源上下文下的多个 asset_id 合并到同一个 resource 请求项中
    const mergedResourceMap = new Map<string, Api.Upload.RenewResourceAccessURLsItemRequest>();
    sanitizedItems.forEach((item) => {
      const ctxKey = `${item.resource_type}:${item.resource_id}`;
      const existing = mergedResourceMap.get(ctxKey);
      if (existing) {
        const combinedAssetIds = Array.from(new Set([...existing.asset_ids, ...item.asset_ids]));
        existing.asset_ids = combinedAssetIds.slice(0, MAX_BATCH_ASSET_COUNT);
      } else {
        mergedResourceMap.set(ctxKey, {
          resource_type: item.resource_type,
          resource_id: item.resource_id,
          asset_ids: item.asset_ids.slice(0, MAX_BATCH_ASSET_COUNT),
        });
      }
    });

    const pendingItems = Array.from(mergedResourceMap.values()).slice(0, MAX_BATCH_RESOURCE_COUNT);
    if (!pendingItems.length) return { items: [], failed: [] };

    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({ resources: pendingItems });
      const successItems = res.items || [];
      set((state) => {
        const nextAssetCache = { ...state.assetCache };
        successItems.forEach((item) => {
          if (item.asset_id && item.access_url) {
            nextAssetCache[item.asset_id] = { asset_id: item.asset_id, url: item.access_url, expires_at: calculateAssetExpiresAt(item.expires_at) };
          }
        });
        return { assetCache: nextAssetCache, renewing: false };
      });
      return res;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  setUserAvatar: (avatar) => set({ userAvatarCache: avatar }),

  renewUserAvatar: async (avatarAssetId, userId) => {
    if (!avatarAssetId) throw new Error('缺失头像资产标识');
    const key = String(avatarAssetId);
    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({
        resources: [{ resource_type: 'ACCOUNT', resource_id: String(userId || key), asset_ids: [key] }],
      });
      const successItem = res.items?.[0];
      if (successItem && successItem.access_url) {
        const expiresAt = calculateAssetExpiresAt(successItem.expires_at);
        set({
          userAvatarCache: { asset_id: key, url: successItem.access_url, expires_at: expiresAt },
          renewing: false,
        });
        return { asset_id: key, access_url: successItem.access_url, expires_at: expiresAt };
      }
      set({ renewing: false });
      return { asset_id: key };
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  setAssetCacheItem: (assetId, url, expiresAt) => {
    if (!assetId || !url) return;
    const validExpires = calculateAssetExpiresAt(expiresAt);
    set((state) => ({
      assetCache: { ...state.assetCache, [assetId]: { asset_id: assetId, url, expires_at: validExpires } },
    }));
  },

  isAssetExpired: (assetId, preheatSeconds = DEFAULT_ASSET_PREHEAT_SECONDS) => {
    const item = get().assetCache[assetId];
    if (!item || typeof item.expires_at !== 'number') return true;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = item.expires_at > 1e11 ? Math.floor(item.expires_at / 1000) : item.expires_at;
    return expiresAt - now <= preheatSeconds;
  },

  startAutoRenewTicker: () => {
    if (get().autoRenewTimerId) return;
    const timerId = setInterval(() => {
      get().triggerAutoRenewCheck().catch((err) => {
        console.error('[AssetRenewStore] 自动巡检续发异常:', err);
      });
    }, 5000);
    set({ autoRenewTimerId: timerId });
  },

  stopAutoRenewTicker: () => {
    const timerId = get().autoRenewTimerId;
    if (timerId) {
      clearInterval(timerId);
      set({ autoRenewTimerId: null });
    }
  },

  triggerAutoRenewCheck: async () => {
    const activeContexts = get().activeResourceContexts;
    const contextKeys = Object.keys(activeContexts);
    if (!contextKeys.length) return;

    const pendingRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    contextKeys.forEach((key) => {
      const ctx = activeContexts[key];
      if (!ctx || !ctx.asset_ids.length) return;
      const expiredAssetIds = ctx.asset_ids.filter((id) => get().isAssetExpired(id));
      if (expiredAssetIds.length) {
        pendingRequests.push({
          resource_type: ctx.resource_type,
          resource_id: ctx.resource_id,
          asset_ids: expiredAssetIds.slice(0, MAX_BATCH_ASSET_COUNT),
        });
      }
    });

    if (!pendingRequests.length) return;

    // 按 resource_type:resource_id 深度合并同资源上下文下的多个待到期 asset_id
    const mergedAutoRenewMap = new Map<string, Api.Upload.RenewResourceAccessURLsItemRequest>();
    pendingRequests.forEach((item) => {
      const ctxKey = `${item.resource_type}:${item.resource_id}`;
      const existing = mergedAutoRenewMap.get(ctxKey);
      if (existing) {
        const combinedAssetIds = Array.from(new Set([...existing.asset_ids, ...item.asset_ids]));
        existing.asset_ids = combinedAssetIds.slice(0, MAX_BATCH_ASSET_COUNT);
      } else {
        mergedAutoRenewMap.set(ctxKey, {
          resource_type: item.resource_type,
          resource_id: item.resource_id,
          asset_ids: item.asset_ids.slice(0, MAX_BATCH_ASSET_COUNT),
        });
      }
    });

    const finalRequests = Array.from(mergedAutoRenewMap.values()).slice(0, MAX_BATCH_RESOURCE_COUNT);

    try {
      const res = await fetchRenewResourceAccessUrls({ resources: finalRequests });
      const successItems = res.items || [];
      if (successItems.length) {
        set((state) => {
          const nextAssetCache = { ...state.assetCache };
          successItems.forEach((item) => {
            if (item.asset_id && item.access_url) {
              nextAssetCache[item.asset_id] = { asset_id: item.asset_id, url: item.access_url, expires_at: calculateAssetExpiresAt(item.expires_at) };
            }
          });
          return { assetCache: nextAssetCache };
        });
      }
    } catch (err) {
      console.error('[AssetRenewStore] 自动巡检发包失败:', err);
    }
  },

  clearPageCache: () => {
    get().stopAutoRenewTicker();
    set({ assetCache: {}, videoAuthCache: {}, activeResourceContexts: {} });
  },

  clearAllCache: () => {
    get().stopAutoRenewTicker();
    set({ assetCache: {}, videoAuthCache: {}, userAvatarCache: null, activeResourceContexts: {} });
  },

  // 默认 clearCache 同步指向 clearPageCache (保护用户头像)
  clearCache: () => {
    get().stopAutoRenewTicker();
    set({ assetCache: {}, videoAuthCache: {}, activeResourceContexts: {} });
  },
}));


